"use client";

import React from "react";
import { Clock } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useParams } from "next/navigation";
import Reveal from "@/components/Sections/Reveal";
import { useFetchNewsArticles } from "@/apis/queryFunctions/news";
import { dictionaries, Locale } from "@/utils/i18n";
import { format } from "date-fns";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";

const SameNews = ({ without }: { without?: string }) => {
  const params = useParams();
  const locale = (params?.locale as Locale) || "vi";
  const dict = dictionaries[locale].news_flow;

  const { data: newsItems, isLoading } = useFetchNewsArticles({
    limit: 4,
    condition: {
      ngon_ngu: locale,
      _id: { $ne: without },
    },
  });

  const items = (newsItems as any[]) || [];

  if (isLoading) {
    return (
      <div className="space-y-8">
        <h3 className="text-xl font-bold text-white border-b border-white/10 pb-4 font-montserrat">
          {dict.related_news}
        </h3>
        <div className="flex flex-col gap-6 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4">
              <div className="w-20 h-14 bg-white/5 rounded-lg flex-shrink-0"></div>
              <div className="flex-1 flex flex-col gap-2">
                <div className="w-16 h-3 bg-white/5 rounded"></div>
                <div className="w-full h-8 bg-white/5 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {items.length > 0 && (
        <>
          <h3 className="text-xl font-bold text-white border-b border-white/10 pb-4 font-montserrat">
            {dict.related_news}
          </h3>

          <div className="flex flex-col gap-6">
            {items.map((news, i) => (
              <Reveal key={news.slug || i} delay={i * 100}>
                <Link
                  href={`/${locale}/tin-tuc/${news.slug}`}
                  className="group flex gap-4 items-start select-none"
                >
                  {/* Thumbnail */}
                  <div className="relative w-20 h-14 sm:w-24 sm:h-16 rounded-lg overflow-hidden bg-white/5 border border-white/10 flex-shrink-0">
                    <Image
                      src={
                        appendImageUrlFromAPI({
                          src: news.picture || news.image,
                          size: "s",
                        }) || "/images/profession1.png"
                      }
                      alt={news.title}
                      fill
                      sizes="(max-width: 640px) 80px, 96px"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  {/* Text Info */}
                  <div className="flex-1 flex flex-col min-w-0">
                    <div className="text-[#DBB653] text-[10px] font-bold uppercase tracking-wider mb-1 truncate">
                      {news.loai_tin_tuc ||
                        dict.categories?.company ||
                        "CCN Tây Bắc Đức Hòa Đông"}
                    </div>
                    <h4 className="font-bold text-white text-xs sm:text-sm leading-snug group-hover:text-[#DBB653] transition-colors line-clamp-2 font-montserrat">
                      {news.title}
                    </h4>
                    <div className="flex items-center gap-1.5 text-white/40 text-[10px] font-medium mt-1">
                      <Clock className="w-3 h-3" />{" "}
                      {news.date_created
                        ? format(new Date(news.date_created), "dd/MM/yyyy")
                        : ""}
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default SameNews;
